import { TypedDocumentNode, gql } from '@apollo/client';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { generatePath, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { catchAll } from '@sorare/routing';

import { AnimatedNumber } from '@sorare/core/src/atoms/animations/AnimatedNumber';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { text16, text20 } from '@sorare/core/src/atoms/typography';
import { MarketingPage } from '@sorare/core/src/components/marketing/MarketingPage';
import { LICENSED_PARTNERS_SPORT as BASE_PATH } from '@sorare/core/src/constants/__generated__/routes';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { MLBTeams } from '@sorare/core/src/lib/mlbTeams';
import { NBATeams } from '@sorare/core/src/lib/nbaTeams';
import { metadatas } from '@sorare/core/src/lib/seo/common';
import {
  desktopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';
import { inferRuleSetProps } from '@sorare/core/src/style/utils';

import { PartnersList } from './PartnersList';
import {
  LicensedClubsQuery,
  LicensedClubsQueryVariables,
} from './__generated__/index.graphql';

const Wrapper = styled(Vertical).attrs({ gap: 0, center: true })`
  width: 100%;
  justify-content: stretch;
`;

const Header = styled(Vertical).attrs({ gap: 0, center: true })`
  justify-content: stretch;
`;

const Count = styled.span`
  --count-font-size: clamp(10rem, 30vw, 20rem);
  font-family: Romie-Italic, serif;
  font-style: italic;
  font-size: var(--count-font-size);
  margin-bottom: calc(-0.35 * var(--count-font-size) + var(--double-unit));

  @media ${tabletAndAbove} {
    margin-bottom: calc(-0.35 * var(--count-font-size) + var(--quadruple-unit));
  }
`;

const Licensed = styled.span`
  font-family: Romie-Italic, serif;
  font-style: italic;
  font-size: clamp(2rem, 10vw, 5rem);
  color: var(--c-brand-300);
`;

const Teams = styled.span`
  font-family:
    Druk Wide,
    sans-serif;
  font-size: clamp(2rem, 6vw, 4rem);
  text-transform: uppercase;
`;

const LicensedTeams = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  @media ${tabletAndAbove} {
    align-items: baseline;
    flex-direction: row;
    gap: var(--double-unit);
  }
  & > *:first-child {
    margin-bottom: calc(-2 * var(--unit));
  }
`;

const Subtitle = styled.div<
  inferRuleSetProps<typeof text16> & inferRuleSetProps<typeof text20>
>`
  color: var(--c-nd-600);
  text-align: center;
  padding: var(--quadruple-unit) 0;
  width: 80%;
  ${text16}
  @media ${tabletAndAbove} {
    ${text20}
    width: 70%;
  }
  @media ${desktopAndAbove} {
    width: 50%;
  }
`;

const TabsWrapper = styled(Horizontal).attrs({ gap: 0, center: true })`
  width: 100%;
  padding-bottom: var(--quadruple-unit);
`;

const LICENSED_CLUBS_QUERY = gql`
  query LicensedClubsQuery {
    football {
      clubsReady {
        slug
        ...PartnersList_club
      }
    }
  }
  ${PartnersList.fragments.club}
` as TypedDocumentNode<LicensedClubsQuery, LicensedClubsQueryVariables>;

const LicensedPartners = () => {
  useTitleAndDescription(
    metadatas.licensedPartners.title,
    metadatas.licensedPartners.description
  );

  const { data, loading } = useQuery(LICENSED_CLUBS_QUERY);
  const [displayedCount, setDisplayedCount] = useState(0);
  const { sport, tab } = useParams();

  useEffect(() => {
    setTimeout(() => {
      setDisplayedCount(
        (data?.football.clubsReady.length || 0) +
          MLBTeams.length +
          NBATeams.length
      );
    }, 0);
  }, [data?.football.clubsReady.length]);
  return (
    <MarketingPage>
      <Header>
        <Wrapper>
          <FormattedMessage
            id="LicensedPartners.title"
            defaultMessage="<a>{count}</a><d><b>licensed</b><c>teams</c></d>"
            values={{
              count: loading ? (
                '300+'
              ) : (
                <AnimatedNumber value={displayedCount} />
              ),
              a: text => <Count>{text}</Count>,
              b: text => <Licensed>{text}</Licensed>,
              c: text => <Teams>{text}</Teams>,
              d: text => <LicensedTeams>{text}</LicensedTeams>,
            }}
          />
        </Wrapper>
        <Subtitle>
          <FormattedMessage
            id="LicensedPartners.subtitle"
            defaultMessage="We have partnered with the best sports leagues and clubs in the world to bring our next-level fantasy sports game to fans across the globe"
          />
        </Subtitle>
      </Header>
      <TabsWrapper>
        <TabBar variant="secondary">
          <Tab
            to={generatePath(BASE_PATH, { sport: 'football' })}
            matches={[catchAll(generatePath(BASE_PATH, { sport: 'football' }))]}
          >
            Football
          </Tab>
          <Tab to={generatePath(BASE_PATH, { sport: 'nba' })}>NBA</Tab>
          <Tab to={generatePath(BASE_PATH, { sport: 'mlb' })}>MLB</Tab>
        </TabBar>
      </TabsWrapper>
      <PartnersList
        footballClubs={data?.football.clubsReady}
        sport={sport}
        tab={tab}
        loading={loading}
      />
    </MarketingPage>
  );
};

export default LicensedPartners;
